import {
  Brain,
  Ban,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  Crown,
  Dices,
  Eye,
  Hand,
  LogOut,
  MessageCircleQuestion,
  Moon,
  Pencil,
  Play,
  RefreshCw,
  Share2,
  ShieldQuestion,
  Sparkles,
  Users,
  Zap
} from "lucide-react";
import QRCode from "qrcode";
import { PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSocketClient, type SocketClient } from "./socketClient";

type GameId =
  | "undercover"
  | "werewolf"
  | "dice"
  | "kings"
  | "memory"
  | "turtle"
  | "draw"
  | "truth"
  | "never"
  | "forbidden"
  | "reaction"
  | "whoami"
  | "charades"
  | "twenty"
  | "describe"
  | "quiz";

interface PlayerSnapshot {
  id: string;
  name: string;
  color: string;
  isHost: boolean;
  isAdmin: boolean;
  connected: boolean;
}

interface RoomSnapshot {
  code: string;
  hostId: string;
  joinLocked: boolean;
  hostOfflineSince?: number;
  you: string;
  canAdmin: boolean;
  adFree?: boolean;
  players: PlayerSnapshot[];
  game: PublicGame;
}

type PublicGame =
  | {
      gameId: "undercover";
      phase: "idle" | "reveal" | "answer";
      round: number;
      bankSize: number;
      imposterCount: number;
      myCard?: { word: string };
      answer?: { civilian: string; imposter: string };
    }
  | {
      gameId: "werewolf";
      phase: "idle" | "reveal" | "night" | "day" | "vote";
      round: number;
      roleSet: string[];
      myRole?: string;
    }
  | {
      gameId: "dice";
      phase: "play";
      round: number;
      diceCount: number;
      sides: number;
      lastRoll?: { playerId: string; playerName: string; dice: number[]; total: number; at: number };
      history: Array<{ playerName: string; dice: number[]; total: number; at: number }>;
    }
  | {
      gameId: "kings";
      phase: "play";
      round: number;
      remaining: number;
      bankSize: number;
      currentIndex: number;
      currentPlayerId?: string;
      currentName?: string;
      isMyTurn?: boolean;
      history: Array<{ playerName: string; card: Card; rule: string; at: number }>;
    }
  | {
      gameId: "memory";
      phase: "play";
      remaining: number;
      bankSize: number;
      historyCount: number;
      lastCard?: { playerName: string; card: Card; at: number };
      revealSequence: boolean;
      history?: Array<{ playerName: string; card: Card; at: number }>;
      currentIndex: number;
      currentPlayerId?: string;
      currentName?: string;
      isMyTurn?: boolean;
    }
  | {
      gameId: "turtle";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      puzzle?: { title: string; setup: string };
      answer?: string;
      log: Array<{ playerName: string; text: string; verdict: "是" | "否" | "无关" | "补充"; at: number }>;
    }
  | {
      gameId: "draw";
      phase: "idle" | "draw" | "answer";
      round: number;
      bankSize: number;
      drawerId?: string;
      drawerName?: string;
      myPrompt?: string;
      strokes: DrawStroke[];
      guesses: Array<{ playerId: string; playerName: string; text: string; correct: boolean; at: number }>;
    }
  | {
      gameId: "truth";
      phase: "play";
      round: number;
      truthBankSize: number;
      dareBankSize: number;
      current?: { mode: "真心话" | "大冒险"; text: string; playerName: string; at: number };
      history: Array<{ mode: "真心话" | "大冒险"; text: string; playerName: string; at: number }>;
    }
  | {
      gameId: "never";
      phase: "play";
      round: number;
      bankSize: number;
      current?: { text: string; playerName: string; at: number };
      history: Array<{ text: string; playerName: string; at: number }>;
    }
  | {
      gameId: "forbidden";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      assignedCount: number;
      othersAssignments: Record<string, string>;
      myAssignmentRevealed?: string;
    }
  | {
      gameId: "reaction";
      phase: "play";
      round: number;
      bankSize: number;
      current?: { text: string; playerName: string; at: number };
      history: Array<{ text: string; playerName: string; at: number }>;
    }
  | {
      gameId: "whoami";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      othersAssignments: Record<string, string>;
      assignedCount: number;
      myAssignmentRevealed?: string;
      guesses: Array<{ playerId: string; playerName: string; guess: string; correct: boolean; at: number }>;
    }
  | {
      gameId: "charades";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      performerId?: string;
      performerName?: string;
      myPrompt?: string;
      guesses: Array<{ playerId: string; playerName: string; text: string; correct: boolean; at: number }>;
    }
  | {
      gameId: "twenty";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      answererId?: string;
      answererName?: string;
      myPrompt?: string;
      questions: Array<{ playerName: string; text: string; at: number }>;
      guesses: Array<{ playerId: string; playerName: string; text: string; correct: boolean; at: number }>;
    }
  | {
      gameId: "describe";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      speakerId?: string;
      speakerName?: string;
      myPrompt?: string;
      guesses: Array<{ playerId: string; playerName: string; text: string; correct: boolean; at: number }>;
    }
  | {
      gameId: "quiz";
      phase: "idle" | "play" | "answer";
      round: number;
      bankSize: number;
      question?: string;
      options: string[];
      answer?: string;
      explanation?: string;
      guesses: Array<{ playerId: string; playerName: string; text: string; correct: boolean; at: number }>;
    };

interface Card {
  suit: "spade" | "heart" | "diamond" | "club";
  rank: string;
  label: string;
}

interface DrawStroke {
  playerId: string;
  color: string;
  width: number;
  points: Array<[number, number]>;
}

const gameMeta: Array<{ id: GameId; name: string; short: string; min: string; icon: typeof ShieldQuestion }> = [
  { id: "undercover", name: "谁是卧底", short: "谁是卧底", min: "3-10", icon: ShieldQuestion },
  { id: "werewolf", name: "狼人杀", short: "身份发牌", min: "2-10", icon: Moon },
  { id: "dice", name: "掷骰子", short: "实时骰盅", min: "2-10", icon: Dices },
  { id: "kings", name: "小姐牌", short: "十三钗", min: "2-10", icon: Crown },
  { id: "memory", name: "记牌挑战", short: "抽牌记忆", min: "2-10", icon: Brain },
  { id: "turtle", name: "海龟汤", short: "问答推理", min: "2-10", icon: CircleHelp },
  { id: "draw", name: "你画我猜", short: "轮流作画", min: "2-10", icon: Pencil },
  { id: "truth", name: "真心话大冒险", short: "随机任务", min: "2-10", icon: MessageCircleQuestion },
  { id: "never", name: "我有你没有", short: "举手破冰", min: "2-10", icon: Hand },
  { id: "forbidden", name: "禁词挑战（害你在心口难开）", short: "诱导触发淘汰", min: "2-10", icon: Ban },
  { id: "reaction", name: "反应小游戏", short: "007/三园", min: "2-10", icon: Zap },
  { id: "whoami", name: "我是谁（猜身份）", short: "猜头上身份", min: "2-10", icon: Users },
  { id: "charades", name: "你演我猜", short: "动作猜词", min: "2-10", icon: Sparkles },
  { id: "twenty", name: "秘密词二十问（是否问答）", short: "是/否推理", min: "2-10", icon: CircleHelp },
  { id: "describe", name: "你说我猜", short: "语言描述", min: "2-10", icon: MessageCircleQuestion },
  { id: "quiz", name: "知识问答", short: "抢答竞赛", min: "2-10", icon: Brain }
];

const gameRules: Record<GameId, string[]> = {
  undercover: ["房主发词后，每人只看自己的词；多数人同词，卧底拿相近词。", "所有人轮流描述并在线下投票，公布答案前不要互看屏幕。", "胜负：卧底全部被投出则平民胜；只剩 2 人且仍有卧底则卧底胜。"],
  werewolf: ["房主随机发身份，手机只负责私密身份和夜晚、白天、投票阶段提示。", "发言、技能、投票和淘汰在线下主持并记录。", "胜负：全部狼人出局则好人胜；狼人数量达到或超过其他存活玩家则狼人胜。"],
  dice: ["任意玩家设置骰子数量和面数后开摇，系统同步并记录点数总和。", "开局前先约定比大小、决定顺序，或由最高/最低点承担任务。", "胜负：本工具没有固定胜负，以现场预先约定的点数规则判定。"],
  kings: ["按房间成员顺序轮流抽牌，没轮到的人不能连抽。", "抽到的牌立即执行；饮酒内容建议替换为喝水、零食或轻量任务。", "胜负：默认没有总冠军；牌抽完或主持人叫停即结束，未完成牌面任务者输本轮。"],
  memory: ["玩家依次抽牌，并在抽牌后按顺序复述此前出现的全部牌。", "需要核对时打开序列；核对结束后可重新隐藏。", "胜负：复述错误者输本轮或淘汰；坚持到最后且没有出错的人获胜。"],
  turtle: ["房主读汤面，其他人提出只能回答“是 / 否 / 无关”的问题。", "根据回答拼出完整事件，房主可记录线索并在适当时揭底。", "胜负：在揭底前还原核心真相即推理成功；本玩法默认没有固定输家。"],
  draw: ["当前画手私密看题并作画，不能写出答案文字；其他人观察并抢猜。", "线下建议口头抢答；远程可在页面提交，自己的正确答案不会泄露给其他玩家。", "胜负：首位猜中者赢得本题；房主公布答案或开始下一题时本题结束，页面暂不累计总分。"],
  truth: ["任意玩家抽取真心话、大冒险或随机题，抽到者现场回答或完成任务。", "不舒适的题目可以跳过，建议不用饮酒或危险动作为惩罚。", "胜负：默认没有输赢；完成题目即通过，大家约定轮数后结束。"],
  never: ["抽一条“我从来没有……”经历题，做过这件事的人举手。", "开局前约定由举手方、未举手方或人数较少的一方承担轻量任务。", "胜负：页面不自动计分；按现场约定判定每轮输方，或只作为无胜负破冰。"],
  forbidden: ["房主分发触发词或动作后，每个人能看到别人的目标，但看不到自己的。", "在自然聊天中诱导对方触发；不能直接念出对方的目标，推荐配合额头便利贴。", "胜负：触发自己目标的人淘汰；坚持到最后仍未触发目标的人获胜。"],
  reaction: ["抽一条 007、逛三园等快节奏规则，所有人立刻按提示依次执行。", "出现停顿、重复、说错或动作错误时，由现场共同确认。", "胜负：首个出错或反应最慢者输本轮；页面不自动累计总分。"],
  whoami: ["每个人能看到其他人的身份，但看不到自己头上的身份。", "轮流提出是/否问题并提交最终猜测；猜测会忽略空格和常见标点。", "胜负：猜对者立即脱出；最后仍未猜出自己身份的人输。"],
  charades: ["当前表演者私密看题，只能用动作和表情表演，不能说话或提示题目中的字。", "线下建议口头抢答；远程可在页面提交，正确答案在揭晓前对其他人隐藏。", "胜负：首位猜中者赢得本题；房主公布答案或开始下一题时本题结束，页面暂不累计总分。"],
  twenty: ["一名回答者私密查看秘密词，其他人合计最多提出 20 个只能用“是 / 否”回答的问题。", "线下直接问答；远程页面用于记录问题和提交最终猜测，分组、计时需主持人现场执行。", "胜负：20 问内猜中则提问方成功；问题用完仍未猜中或主动放弃则回答者胜。"],
  describe: ["描述者私密看题并用语言提示，但不能说出题目中的字、谐音或直接翻译。", "线下建议口头抢答；远程可在页面提交，正确答案在揭晓前对其他人隐藏。", "胜负：首位猜中者赢得本题；房主公布答案或开始下一题时本题结束，页面暂不累计总分。"],
  quiz: ["房主抽取常识题，玩家线下口头抢答，或在页面点击选项、输入答案。", "线上提交会立即反馈本人正误；其他人的具体答案在揭晓前保持隐藏。", "胜负：答对者赢得本题；如需分组总分或限时，由主持人现场记录，页面暂不自动计分。"]
};

const gameGuideLinks: Record<GameId, string> = {
  undercover: "/online-who-is-undercover",
  werewolf: "/play/werewolf",
  dice: "/play/dice",
  kings: "/play/thirteen-cards",
  memory: "/play/memory-cards",
  turtle: "/play/turtle-soup",
  draw: "/play/draw-and-guess",
  truth: "/play/truth-or-dare",
  never: "/play/never-have-i-ever",
  forbidden: "/play/taboo-words",
  reaction: "/play/reaction-games",
  whoami: "/play/who-am-i",
  charades: "/play/charades",
  twenty: "/play/twenty-questions",
  describe: "/play/describe-and-guess",
  quiz: "/play/quiz"
};

const storage = {
  name: "party-room-name",
  playerId: "party-room-player-id",
  secret: "party-room-secret",
  code: "party-room-code",
  adFreeToken: "party-room-vip"
};

const CLIENT_METADATA_TIMEOUT_MS = 1200;

type ClientMetadata = {
  timeZone?: string;
  language?: string;
  languages?: string[];
  deviceModel?: string;
  platform?: string;
  platformVersion?: string;
  referrer?: string;
  campaignSource?: string;
  campaignMedium?: string;
  campaignName?: string;
};

function getClientMetadata(): ClientMetadata {
  const params = new URLSearchParams(location.search);
  return {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    language: navigator.language || "",
    languages: Array.from(navigator.languages || []),
    deviceModel: localStorage.getItem("party-room-device-model") || "",
    platform: localStorage.getItem("party-room-platform") || "",
    platformVersion: localStorage.getItem("party-room-platform-version") || "",
    referrer: (() => {
      try {
        return document.referrer ? new URL(document.referrer).hostname : "";
      } catch {
        return "";
      }
    })(),
    campaignSource: params.get("utm_source") || "",
    campaignMedium: params.get("utm_medium") || "",
    campaignName: params.get("utm_campaign") || ""
  };
}

async function refreshClientMetadata(): Promise<ClientMetadata> {
  const metadata = getClientMetadata();
  try {
    const uaData = (navigator as Navigator & {
      userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platform?: string; platformVersion?: string }> };
    }).userAgentData;
    const hints = uaData?.getHighEntropyValues ? await uaData.getHighEntropyValues(["model", "platform", "platformVersion"]) : undefined;
    metadata.deviceModel = hints?.model || metadata.deviceModel || "";
    metadata.platform = hints?.platform || metadata.platform || "";
    metadata.platformVersion = hints?.platformVersion || metadata.platformVersion || "";
  } catch {
    // High-entropy UA hints are browser-dependent; keep stable language/timezone fields.
  }
  if (metadata.deviceModel) localStorage.setItem("party-room-device-model", metadata.deviceModel);
  if (metadata.platform) localStorage.setItem("party-room-platform", metadata.platform);
  if (metadata.platformVersion) localStorage.setItem("party-room-platform-version", metadata.platformVersion);
  return metadata;
}

async function clientMetadataWithTimeout(timeoutMs = CLIENT_METADATA_TIMEOUT_MS): Promise<ClientMetadata> {
  const fallback = getClientMetadata();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      refreshClientMetadata(),
      new Promise<ClientMetadata>((resolve) => {
        timer = setTimeout(() => resolve(fallback), timeoutMs);
      })
    ]);
  } catch {
    return fallback;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function App() {
  const socket = useMemo<SocketClient>(() => createSocketClient(), []);
  const requestedGame = useMemo<GameId>(() => {
    const raw = new URLSearchParams(location.search).get("game");
    return gameMeta.some((game) => game.id === raw) ? (raw as GameId) : "undercover";
  }, []);
  const [name, setName] = useState(() => localStorage.getItem(storage.name) || "");
  const [joinCode, setJoinCode] = useState(() => new URLSearchParams(location.search).get("room") || "");
  const [playerId, setPlayerId] = useState(() => localStorage.getItem(storage.playerId) || "");
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inviteQr, setInviteQr] = useState("");
  // True while a create/join request is in flight, so a double-tap can't fire a duplicate
  // join (which the server would reject as a name clash) or spin up two rooms.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // Live socket connection state. Starts true so the home screen never flashes a banner;
  // only the in-room view surfaces "reconnecting" when this goes false (see conn-banner).
  const [connected, setConnected] = useState(true);
  // Tracks the active room session so we can silently re-join after a transient socket
  // reconnect (phone lock / weak signal). Only set once we are actually in a room, so the
  // first connect on a fresh load does not change the existing auto-restore behaviour.
  const sessionRef = useRef<{ code: string; playerId: string } | null>(null);
  const clearRoomSession = useCallback((message = "") => {
    sessionRef.current = null;
    submittingRef.current = false;
    setSubmitting(false);
    setRoom(null);
    setPlayerId("");
    localStorage.removeItem(storage.code);
    localStorage.removeItem(storage.playerId);
    localStorage.removeItem(storage.secret);
    setError(message);
  }, []);

  useEffect(() => {
    if (room && playerId) sessionRef.current = { code: room.code, playerId };
  }, [room, playerId]);

  useEffect(() => {
    if (!room) return;
    refreshClientMetadata()
      .then((metadata) => {
        fetch("/api/track", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            path: location.pathname,
            roomCode: room.code,
            ...metadata
          }),
          keepalive: true,
          cache: "no-store"
        }).catch(() => {});
      })
      .catch(() => {});
  }, [room?.code]);

  useEffect(() => {
    async function handleConnect() {
      setConnected(true);
      // Silently re-join the active room after a transient reconnect (phone lock / weak
      // signal). No-op on the first connect of a fresh load, since sessionRef is only set
      // once we are actually in a room — so this never double-joins during create/join.
      const session = sessionRef.current;
      if (!session) return;
      socket.emit(
        "room:join",
        {
          code: session.code,
          playerId: session.playerId,
          secret: localStorage.getItem(storage.secret) || undefined,
          adFreeToken: localStorage.getItem(storage.adFreeToken) || undefined,
          client: await clientMetadataWithTimeout()
        },
        (response: { ok: boolean; error?: string; room?: RoomSnapshot; playerId?: string; secret?: string }) => {
          if (response?.ok && response.room) {
            setRoom(response.room);
            if (response.playerId) setPlayerId(response.playerId);
            if (response.secret) localStorage.setItem(storage.secret, response.secret);
            setError("");
            return;
          }
          clearRoomSession(response?.error ? `无法恢复上次房间：${response.error}` : "上次房间已失效，请重新加入");
        }
      );
    }
    function handleDisconnect() {
      setConnected(false);
    }
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [clearRoomSession, socket]);

  useEffect(() => {
    function handleRoomUpdate(nextRoom: RoomSnapshot) {
      setRoom(nextRoom);
      localStorage.setItem(storage.code, nextRoom.code);
    }
    function handleRoomKicked() {
      clearRoomSession("你已被房主或管理员移出房间");
    }
    socket.on("room:update", handleRoomUpdate);
    socket.on("room:kicked", handleRoomKicked);
    return () => {
      socket.off("room:update", handleRoomUpdate);
      socket.off("room:kicked", handleRoomKicked);
    };
  }, [clearRoomSession, socket]);

  useEffect(() => {
    // Incremental draw strokes arrive on their own channel so a long drawing does not
    // rebroadcast the whole board. Append to the current draw game's strokes; the canvas
    // renders only the new stroke (no full repaint), avoiding flicker.
    function onDrawStroke(payload: { stroke?: DrawStroke }) {
      if (!payload?.stroke) return;
      setRoom((prev) => {
        if (!prev || prev.game.gameId !== "draw") return prev;
        return { ...prev, game: { ...prev.game, strokes: [...prev.game.strokes, payload.stroke!] } };
      });
    }
    socket.on("draw:stroke", onDrawStroke);
    return () => {
      socket.off("draw:stroke", onDrawStroke);
    };
  }, [socket]);

  useEffect(() => {
    const savedCode = localStorage.getItem(storage.code);
    const savedId = localStorage.getItem(storage.playerId);
    if (!room && savedCode && savedId) {
      joinRoom(savedCode, savedId);
    }
    // Auto-restore should run once after the socket listener is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isHost = Boolean(room && room.you === room.hostId);
  const canManageGame = Boolean(room?.canAdmin);
  const selfName = room?.players.find((player) => player.id === room.you)?.name || "";
  const inviteLink = room ? `${location.origin}${location.pathname}?room=${room.code}` : "";

  useEffect(() => {
    if (!inviteLink) {
      setInviteQr("");
      return;
    }
    let active = true;
    QRCode.toDataURL(inviteLink, { margin: 1, width: 180 })
      .then((url) => {
        if (active) setInviteQr(url);
      })
      .catch(() => {
        if (active) setInviteQr("");
      });
    return () => {
      active = false;
    };
  }, [inviteLink]);

  function handleAck(response: { ok: boolean; error?: string; room?: RoomSnapshot; playerId?: string; secret?: string }) {
    submittingRef.current = false;
    setSubmitting(false);
    if (!response?.ok) {
      setError(response?.error || "操作失败");
      return;
    }
    if (response.playerId) {
      setPlayerId(response.playerId);
      localStorage.setItem(storage.playerId, response.playerId);
    }
    if (response.secret) {
      localStorage.setItem(storage.secret, response.secret);
    }
    if (response.room) {
      setRoom(response.room);
      localStorage.setItem(storage.code, response.room.code);
    }
    setError("");
  }

  function saveName() {
    const trimmed = name.trim();
    if (trimmed) localStorage.setItem(storage.name, trimmed);
    else localStorage.removeItem(storage.name);
  }

  function beginSubmission(): boolean {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setSubmitting(true);
    return true;
  }

  // Ad-free token: read once from ?vip= URL param (sticky-saved to localStorage so
  // the bookmark / refresh keeps working without the URL hash). Whitelisted clients
  // get the AdSlot hidden via the server's room.adFree flag.
  function getAdFreeToken(): string | undefined {
    const fromUrl = new URLSearchParams(location.search).get("vip");
    if (fromUrl) {
      localStorage.setItem(storage.adFreeToken, fromUrl);
      return fromUrl;
    }
    return localStorage.getItem(storage.adFreeToken) || undefined;
  }

  async function createRoom() {
    if (!beginSubmission()) return;
    saveName();
    socket.emit(
      "room:create",
      { name: name.trim() || undefined, adFreeToken: getAdFreeToken(), client: await clientMetadataWithTimeout() },
      (response: { ok: boolean; error?: string; room?: RoomSnapshot; playerId?: string; secret?: string }) => {
        handleAck(response);
        if (response?.ok && response.room && response.playerId && requestedGame !== "undercover") {
          socket.emit("room:action", {
            roomCode: response.room.code,
            playerId: response.playerId,
            type: "select-game",
            payload: { gameId: requestedGame }
          });
        }
      }
    );
  }

  async function joinRoom(code = joinCode, existingPlayerId = "") {
    if (!beginSubmission()) return;
    const normalizedCode = code.trim().toUpperCase();
    // Validate length client-side for a fresh join (saved-session reconnects pass an
    // existingPlayerId and a code we already trusted, so skip the check there).
    if (!existingPlayerId && normalizedCode.length !== 4) {
      submittingRef.current = false;
      setSubmitting(false);
      setError("请输入 4 位房间码");
      return;
    }
    saveName();
    socket.emit(
      "room:join",
      {
        code: normalizedCode,
        name: existingPlayerId ? undefined : name.trim() || undefined,
        playerId: existingPlayerId || undefined,
        secret: existingPlayerId ? localStorage.getItem(storage.secret) || undefined : undefined,
        adFreeToken: getAdFreeToken(),
        client: await clientMetadataWithTimeout()
      },
      (response: { ok: boolean; error?: string; room?: RoomSnapshot; playerId?: string; secret?: string }) => {
        if (existingPlayerId && !response?.ok) {
          clearRoomSession(response?.error ? `无法恢复上次房间：${response.error}` : "上次房间已失效，请重新加入");
          return;
        }
        handleAck(response);
      }
    );
  }

  function action(type: string, payload?: unknown) {
    if (!room || !playerId) return;
    socket.emit("room:action", { roomCode: room.code, playerId, type, payload }, (response: { ok: boolean; error?: string }) => {
      if (!response?.ok) setError(response?.error || "操作失败");
    });
  }

  async function copyInvite() {
    if (!room) return;
    const ok = await copyText(inviteLink);
    setNotice(ok ? "邀请链接已复制" : "复制失败，请手动分享房间码");
    window.setTimeout(() => setNotice(""), 1800);
  }

  async function copyRoomCode() {
    if (!room) return;
    const ok = await copyText(room.code);
    setNotice(ok ? "房间码已复制" : "复制失败，请手动输入房间码");
    window.setTimeout(() => setNotice(""), 1800);
  }

  function leaveRoom() {
    socket.emit("room:leave");
    clearRoomSession();
  }

  if (!room) {
    return (
      <main className="shell home-shell">
        <section className="hero-band">
          <div className="brand-mark">
            <Sparkles size={22} />
          </div>
          <div>
            <h1>聚会开局</h1>
            <p className="hero-copy">2-10 人，扫码或房间码加入，桌边各拿一部手机就能玩。</p>
          </div>
        </section>

        <section className="join-panel">
          <label>
            <span>你的昵称</span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="不填则自动分配成员1/成员2" maxLength={12} />
            <small className="name-hint">进房前建议设置昵称；同一房间内昵称不能重复。</small>
          </label>

          <button className="primary-btn" onClick={createRoom} disabled={submitting}>
            <Play size={18} />
            {submitting ? "创建中…" : "创建房间"}
          </button>

          <div className="join-row">
            <input
              value={joinCode}
              onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
              placeholder="输入 4 位房间码"
              maxLength={4}
              inputMode="text"
              autoCapitalize="characters"
            />
            <button className="icon-btn wide" onClick={() => joinRoom()} title="加入房间" disabled={submitting}>
              <Users size={18} />
              加入
            </button>
          </div>

          {error && <p className="error-text">{error}</p>}
        </section>

        <ScrollRail className="market-strip" controls label="全部 16 款聚会游戏">
          {gameMeta.map((game) => {
            const Icon = game.icon;
            return (
              <div className="game-chip" key={game.id}>
                <Icon size={17} />
                <span>{game.name}</span>
              </div>
            );
          })}
        </ScrollRail>

        <HomeIntro />
        <SiteFooter />
      </main>
    );
  }

  return (
    <main className="shell app-shell">
      <header className="room-header">
        <div>
          <p className="eyebrow">房间码</p>
          <h1>{room.code}</h1>
          <p className="self-name">你是 {selfName || "成员"}</p>
        </div>
        <div className="header-actions">
          <button className="icon-btn" onClick={copyInvite} title="复制邀请链接">
            <Share2 size={18} />
          </button>
          <button className="icon-btn" onClick={copyRoomCode} title="复制房间码">
            <Copy size={18} />
          </button>
          <button className="icon-btn" onClick={leaveRoom} title="退出房间">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {!connected && (
        <div className="conn-banner" role="status" aria-live="polite">
          <RefreshCw size={15} />
          <span>连接已断开，正在自动重连…重连成功后会自动回到房间。</span>
        </div>
      )}

      {notice && <div className="toast">{notice}</div>}
      {error && <div className="toast error">{error}</div>}

      <div className="room-body">
        <aside className="room-side">
          <PlayerRail players={room.players} viewerId={room.you} />
          <p className="admin-hint">房主/管理员能切换游戏、开局发牌、重置、踢人和锁房；设管理员仍由房主执行。</p>
          <AdminPanel room={room} isHost={isHost} action={action} />

          <ScrollRail className="game-selector">
            {gameMeta.map((game) => {
              const Icon = game.icon;
              const active = room.game.gameId === game.id;
              return (
                <button
                  key={game.id}
                  className={active ? "selector-card active" : "selector-card"}
                  disabled={!canManageGame}
                  onClick={() => action("select-game", { gameId: game.id })}
                >
                  <Icon size={19} />
                  <span>{game.name}</span>
                  <small>{game.min}</small>
                </button>
              );
            })}
          </ScrollRail>
        </aside>

        <div className="room-main">
          <GamePanel room={room} isHost={canManageGame} action={action} />
          <GameRules gameId={room.game.gameId} />
          <InviteQr code={room.code} link={inviteLink} qr={inviteQr} />
          {!room.adFree && <AdSlot />}
        </div>
      </div>

      <SiteFooter />
    </main>
  );
}

function PlayerRail({ players, viewerId }: { players: PlayerSnapshot[]; viewerId: string }) {
  return (
    <ScrollRail className="player-rail">
      {players.map((player) => (
        <div className={`${player.connected ? "player-pill" : "player-pill offline"}${player.id === viewerId ? " self" : ""}`} key={player.id}>
          <span className="avatar-dot" style={{ background: player.color }} />
          <span>{player.name}{player.id === viewerId ? "（你）" : ""}</span>
          {player.isHost && <Crown size={14} />}
          {player.isAdmin && !player.isHost && <span className="admin-badge">管</span>}
        </div>
      ))}
    </ScrollRail>
  );
}

function AdminPanel({
  room,
  isHost,
  action
}: {
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const self = room.players.find((player) => player.id === room.you);
  const host = room.players.find((player) => player.id === room.hostId);
  const canAdmin = Boolean(self && (self.isHost || self.isAdmin));
  const canClaimHost = Boolean(self?.isAdmin && !self.isHost && host && !host.connected);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  function startRename(playerId: string, currentName: string) {
    setEditingId(playerId);
    setDraftName(currentName);
  }
  function cancelRename() {
    setEditingId(null);
    setDraftName("");
  }
  function commitRename(playerId: string, originalName: string) {
    const next = draftName.trim().slice(0, 12);
    if (!next || next === originalName) {
      cancelRename();
      return;
    }
    action("rename-player", { playerId, name: next });
    cancelRename();
  }
  return (
    <section className="admin-panel">
      <div className="admin-head">
        <span>房间管理</span>
      </div>
      <p>切换游戏、开局发牌、重置、锁房、踢人可由房主/管理员执行；设管理员仍由房主执行。</p>
      {canAdmin && (
        <label className="join-switch">
          <button
            type="button"
            role="switch"
            aria-checked={!room.joinLocked}
            aria-label={room.joinLocked ? "当前已锁房，点击允许加入" : "当前允许加入，点击锁房"}
            className={`switch-track${room.joinLocked ? "" : " on"}`}
            onClick={() => action("toggle-join-lock", { locked: !room.joinLocked })}
          >
            <span className="switch-thumb" />
          </button>
          <span className="switch-text">
            {room.joinLocked ? "已锁房（不允许新成员加入）" : "允许加入"}
          </span>
        </label>
      )}
      {canAdmin && canClaimHost && (
        <div className="action-row">
          <button className="ghost-btn" onClick={() => action("claim-host")}>
            接管房主
          </button>
        </div>
      )}
      {canAdmin && (
        <div className="member-actions">
          {room.players
            .filter((player) => player.id !== room.you)
            .map((player) => {
              const editing = editingId === player.id;
              return (
                <div className="member-action" key={player.id}>
                  {editing ? (
                    <input
                      className="rename-input"
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") commitRename(player.id, player.name);
                        if (event.key === "Escape") cancelRename();
                      }}
                      maxLength={12}
                      autoFocus
                    />
                  ) : (
                    <span>
                      {player.name}
                      {player.isHost ? " · 房主" : player.isAdmin ? " · 管理员" : ""}
                    </span>
                  )}
                  <div>
                    {editing ? (
                      <>
                        <button className="mini-btn" onClick={() => commitRename(player.id, player.name)}>
                          保存
                        </button>
                        <button className="mini-btn" onClick={cancelRename}>
                          取消
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="mini-btn" onClick={() => startRename(player.id, player.name)}>
                          改名
                        </button>
                        {isHost && !player.isHost && (
                          <button className="mini-btn" onClick={() => action("set-admin", { playerId: player.id, isAdmin: !player.isAdmin })}>
                            {player.isAdmin ? "取消管理员" : "设为管理员"}
                          </button>
                        )}
                        {!player.isHost && (
                          <button className="mini-btn danger" onClick={() => action("kick-player", { playerId: player.id })}>
                            踢出
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </section>
  );
}

// Horizontal scroller that fades its edges only when there is more content to scroll to,
// so the swipe affordance is discoverable. Room-side rails become vertical on desktop (CSS),
// while the home rail keeps its explicit controls. Subscribes once on mount; the ResizeObserver re-runs the
// edge check whenever content or size changes (e.g. players join), and the functional state
// update returns the prior object when unchanged so no extra renders are triggered.
function ScrollRail({
  className,
  children,
  controls = false,
  label
}: {
  className: string;
  children: React.ReactNode;
  controls?: boolean;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 4;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  const scrollByPage = (direction: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(240, el.clientWidth * 0.72),
      behavior: "smooth"
    });
  };

  return (
    <div
      className={`scroll-rail${controls ? " has-controls" : ""}${edges.left ? " fade-left" : ""}${edges.right ? " fade-right" : ""}`}
      role={label ? "region" : undefined}
      aria-label={label}
    >
      {controls && (
        <button
          type="button"
          className="rail-button rail-button-left"
          onClick={() => scrollByPage(-1)}
          disabled={!edges.left}
          aria-label="向左查看更多游戏"
          title="向左查看更多游戏"
        >
          <ChevronLeft size={20} />
        </button>
      )}
      <div className={className} ref={ref} tabIndex={controls ? 0 : undefined}>
        {children}
      </div>
      {controls && (
        <button
          type="button"
          className="rail-button rail-button-right"
          onClick={() => scrollByPage(1)}
          disabled={!edges.right}
          aria-label="向右查看更多游戏"
          title="向右查看更多游戏"
        >
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
}

function GamePanel({ room, isHost, action }: { room: RoomSnapshot; isHost: boolean; action: (type: string, payload?: unknown) => void }) {
  switch (room.game.gameId) {
    case "undercover":
      return <UndercoverPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "werewolf":
      return <WerewolfPanel game={room.game} isHost={isHost} action={action} />;
    case "dice":
      return <DicePanel game={room.game} action={action} />;
    case "kings":
      return <KingsPanel game={room.game} isHost={isHost} action={action} />;
    case "memory":
      return <MemoryPanel game={room.game} isHost={isHost} action={action} />;
    case "turtle":
      return <TurtlePanel game={room.game} isHost={isHost} action={action} />;
    case "draw":
      return <DrawPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "truth":
      return <TruthPanel game={room.game} action={action} />;
    case "never":
      return <NeverPanel game={room.game} action={action} />;
    case "forbidden":
      return <ForbiddenPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "reaction":
      return <ReactionPanel game={room.game} action={action} />;
    case "whoami":
      return <WhoAmIPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "charades":
      return <CharadesPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "twenty":
      return <TwentyPanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "describe":
      return <DescribePanel game={room.game} room={room} isHost={isHost} action={action} />;
    case "quiz":
      return <QuizPanel game={room.game} room={room} isHost={isHost} action={action} />;
    default:
      return null;
  }
}

function UndercoverPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "undercover" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const onlineCount = Math.max(1, room.players.filter((player) => player.connected).length);
  const maxImposters = maxUndercoverCount(onlineCount);
  const currentCount = Math.min(maxImposters, Math.max(1, game.imposterCount || 1));
  const setCount = (count: number) => action("set-undercover-count", { count });
  return (
    <section className="play-panel undercover-scene">
      <PanelTitle title="谁是卧底" subtitle="也叫词汇间谍" icon={<ShieldQuestion size={22} />} meta={`第 ${game.round || 0} 局 · 题库 ${game.bankSize} 对`} />
      <div className="secret-card word-card">
        <span>你的词</span>
        <strong>{game.myCard?.word || "等待发词"}</strong>
      </div>
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-undercover")}>
            <Play size={18} />
            发词开局
          </button>
        )}
        {isHost && game.phase === "reveal" && (
          <button className="ghost-btn" onClick={() => action("reveal-undercover")}>
            <Eye size={18} />
            公布答案
          </button>
        )}
      </div>
      <div className="setting-row">
        <div>
          <span>卧底数量</span>
          <strong>{currentCount}</strong>
          <small>最多 {maxImposters} 个（不超过在线人数 1/3）</small>
        </div>
        {isHost && (
          <div className="stepper" aria-label="调整卧底数量">
            <button className="icon-btn" onClick={() => setCount(currentCount - 1)} disabled={currentCount <= 1}>-</button>
            <button className="icon-btn" onClick={() => setCount(currentCount + 1)} disabled={currentCount >= maxImposters}>+</button>
          </div>
        )}
      </div>
      {game.answer && (
        <div className="answer-band">
          <span>多数词：{game.answer.civilian}</span>
          <span>少数词：{game.answer.imposter}</span>
        </div>
      )}
      <p className="microcopy">本局有 {game.imposterCount} 个卧底；当前题库 {game.bankSize} 对词，轮流描述后现场投票。</p>
    </section>
  );
}

function WerewolfPanel({ game, isHost, action }: { game: Extract<PublicGame, { gameId: "werewolf" }>; isHost: boolean; action: (type: string, payload?: unknown) => void }) {
  const phases = [
    ["night", "夜晚"],
    ["day", "白天"],
    ["vote", "投票"]
  ];
  return (
    <section className="play-panel werewolf-scene">
      <PanelTitle title="狼人杀发牌" subtitle="线下讨论，手机只负责身份和阶段" icon={<Moon size={22} />} meta={`第 ${game.round || 0} 局`} />
      <div className="secret-card role-card">
        <span>你的身份</span>
        <strong>{game.myRole || "等待发牌"}</strong>
      </div>
      <div className="role-grid">
        {(game.roleSet.length ? game.roleSet : ["按人数自动配置"]).map((role, index) => (
          <span key={`${role}-${index}`}>{role}</span>
        ))}
      </div>
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-werewolf")}>
            <Play size={18} />
            随机发牌
          </button>
        )}
        {isHost &&
          phases.map(([phase, label]) => (
            <button key={phase} className="ghost-btn" onClick={() => action("set-werewolf-phase", { phase })}>
              {label}
            </button>
          ))}
      </div>
      <p className="microcopy">当前阶段：{phaseLabel(game.phase)}</p>
    </section>
  );
}

function DicePanel({ game, action }: { game: Extract<PublicGame, { gameId: "dice" }>; action: (type: string, payload?: unknown) => void }) {
  const [count, setCount] = useState(game.diceCount || 2);
  const [sides, setSides] = useState(game.sides || 6);
  return (
    <section className="play-panel dice-scene">
      <PanelTitle title="掷骰子" subtitle="摇骰、比大小、临时惩罚都能用" icon={<Dices size={22} />} meta={`第 ${game.round || 0} 轮`} />
      <div className="dice-controls">
        <label>
          <span>数量</span>
          <input type="number" min={1} max={8} value={count} onChange={(event) => setCount(clampInt(event.target.value, 1, 8, count))} />
        </label>
        <label>
          <span>面数</span>
          <input type="number" min={2} max={100} value={sides} onChange={(event) => setSides(clampInt(event.target.value, 2, 100, sides))} />
        </label>
        <button className="primary-btn" onClick={() => action("roll-dice", { count, sides })}>
          <Dices size={18} />
          开摇
        </button>
      </div>
      <div className="dice-board">
        {(game.lastRoll?.dice || [1, 1]).map((value, index) => (
          <span className="die" key={`${value}-${index}`}>
            {value}
          </span>
        ))}
      </div>
      {game.lastRoll && <p className="score-line">{game.lastRoll.playerName} 掷出 {game.lastRoll.total}</p>}
      <HistoryList items={game.history.map((item) => `${item.playerName}: ${item.dice.join(" + ")} = ${item.total}`)} />
    </section>
  );
}

function KingsPanel({ game, isHost, action }: { game: Extract<PublicGame, { gameId: "kings" }>; isHost: boolean; action: (type: string) => void }) {
  const top = game.history[0];
  return (
    <section className="play-panel kings-scene">
      <PanelTitle title="小姐牌" subtitle="也叫金陵十三钗，惩罚可替换成喝水或零食" icon={<Crown size={22} />} meta={`第 ${game.round || 0} 张 · 牌库 ${game.bankSize} 张 · 轮到 ${game.currentName || "下一位"}`} />
      <div className="card-stage">
        {top ? <PlayingCard card={top.card} /> : <div className="card-back">?</div>}
        <div>
          <p className="remaining">剩余 {game.remaining} 张</p>
          <strong>{top?.rule || "抽一张牌开始"}</strong>
        </div>
      </div>
      <div className="action-row">
        <button className="primary-btn" disabled={!game.isMyTurn} onClick={() => action("draw-kings")}>
          <Play size={18} />
          {game.isMyTurn ? "抽牌" : `等待 ${game.currentName || "下一位"}`}
        </button>
        {isHost && (
          <button className="ghost-btn" onClick={() => action("reset-kings")}>
            <RefreshCw size={18} />
            洗牌
          </button>
        )}
      </div>
      <HistoryList items={game.history.map((item) => `${item.playerName} 抽到 ${item.card.label}: ${item.rule}`)} />
    </section>
  );
}

function MemoryPanel({
  game,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "memory" }>;
  isHost: boolean;
  action: (type: string) => void;
}) {
  return (
    <section className="play-panel memory-scene">
      <PanelTitle title="抽牌记牌" subtitle="每人抽牌后复述之前序列，错了现场惩罚" icon={<Brain size={22} />} meta={`第 ${game.historyCount || 0} 张 · 牌库 ${game.bankSize} 张 · 轮到 ${game.currentName || "下一位"}`} />
      <div className="card-stage">
        {game.lastCard ? <PlayingCard card={game.lastCard.card} /> : <div className="card-back">?</div>}
        <div>
          <p className="remaining">第 {game.historyCount + 1} 张，轮到 {game.currentName || "下一位"}</p>
          <strong>{game.lastCard ? `${game.lastCard.playerName} 抽到 ${game.lastCard.card.label}` : "抽一张牌开始"}</strong>
        </div>
      </div>
      <div className="action-row">
        <button className="primary-btn" disabled={!game.isMyTurn} onClick={() => action("draw-memory")}>
          <Play size={18} />
          {game.isMyTurn ? "抽牌" : `等待 ${game.currentName || "下一位"}`}
        </button>
        <button className="ghost-btn" onClick={() => action("toggle-memory")}>
          <Eye size={18} />
          {game.revealSequence ? "隐藏序列" : "核对序列"}
        </button>
        {isHost && (
          <button className="ghost-btn" onClick={() => action("reset-memory")}>
            <RefreshCw size={18} />
            重开
          </button>
        )}
      </div>
      <HistoryList items={(game.history || []).map((item, index) => `${index + 1}. ${item.card.label} (${item.playerName})`)} />
    </section>
  );
}

function TurtlePanel({ game, isHost, action }: { game: Extract<PublicGame, { gameId: "turtle" }>; isHost: boolean; action: (type: string, payload?: unknown) => void }) {
  const [text, setText] = useState("");
  const [verdict, setVerdict] = useState<"是" | "否" | "无关" | "补充">("补充");
  function submitNote() {
    if (!text.trim()) return;
    action("soup-note", { text, verdict });
    setText("");
  }
  return (
    <section className="play-panel turtle-scene">
      <PanelTitle title="海龟汤" subtitle="一人读题，大家只能问是/否问题" icon={<CircleHelp size={22} />} meta={`第 ${game.round || 0} 题 · 题库 ${game.bankSize} 题`} />
      <div className="soup-box">
        <p>{game.puzzle?.title || "等待开汤"}</p>
        <strong>{game.puzzle?.setup || "房主抽一题，其他人现场提问。"}</strong>
      </div>
      {game.answer && <div className="answer-band">{game.answer}</div>}
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-soup")}>
            <Play size={18} />
            开新汤
          </button>
        )}
        {isHost && game.puzzle && (
          <button className="ghost-btn" onClick={() => action("reveal-soup")}>
            <Eye size={18} />
            揭底
          </button>
        )}
      </div>
      <div className="note-row">
        <select value={verdict} onChange={(event) => setVerdict(event.target.value as typeof verdict)}>
          <option>补充</option>
          <option>是</option>
          <option>否</option>
          <option>无关</option>
        </select>
        <input value={text} onChange={(event) => setText(event.target.value)} placeholder="记录一个问题或线索" maxLength={80} />
        <button className="icon-btn wide" onClick={submitNote}>
          记录
        </button>
      </div>
      <HistoryList items={game.log.map((item) => `${item.verdict}: ${item.text} (${item.playerName})`)} />
    </section>
  );
}

function DrawPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "draw" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointsRef = useRef<Array<[number, number]>>([]);
  const drawingRef = useRef(false);
  const renderedStrokesRef = useRef<DrawStroke[]>([]);
  const locallyDrawnStrokesRef = useRef<DrawStroke[]>([]);
  const [guess, setGuess] = useState("");
  const isDrawer = room.you === game.drawerId;
  const myCorrectGuess = game.guesses.find((item) => item.playerId === room.you && item.correct);

  function paintBoard(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#fffaf1";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Paint the board background once per mount and replay any strokes already present
  // (e.g. reconnecting mid-drawing).
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    paintBoard(context, canvas);
    game.strokes.forEach((stroke) => drawStroke(context, stroke, canvas.width, canvas.height));
    renderedStrokesRef.current = game.strokes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render incrementally while the server snapshot still extends the board we already
  // rendered. If a reconnect/resync replaces strokes without changing the array length,
  // repaint from scratch so stale drawings cannot survive on the canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const strokes = game.strokes;
    const renderedStrokes = renderedStrokesRef.current;
    const extendsRenderedBoard =
      renderedStrokes.length <= strokes.length &&
      renderedStrokes.every((stroke, index) => sameDrawStroke(stroke, strokes[index]));
    if (!extendsRenderedBoard) {
      paintBoard(context, canvas);
      strokes.forEach((stroke) => drawStroke(context, stroke, canvas.width, canvas.height));
      locallyDrawnStrokesRef.current = [];
      renderedStrokesRef.current = strokes;
      return;
    }
    for (let index = renderedStrokes.length; index < strokes.length; index += 1) {
      const stroke = strokes[index];
      const pendingIndex = locallyDrawnStrokesRef.current.findIndex((pending) => sameDrawStroke(stroke, pending));
      if (pendingIndex >= 0) {
        locallyDrawnStrokesRef.current.splice(pendingIndex, 1);
        continue;
      }
      drawStroke(context, stroke, canvas.width, canvas.height);
    }
    renderedStrokesRef.current = strokes;
  }, [game.strokes]);

  function getPoint(event: PointerEvent<HTMLCanvasElement>): [number, number] {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return [clamp((event.clientX - rect.left) / rect.width), clamp((event.clientY - rect.top) / rect.height)];
  }

  function pointerDown(event: PointerEvent<HTMLCanvasElement>) {
    if (!isDrawer || game.phase !== "draw") return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointsRef.current = [getPoint(event)];
    drawingRef.current = true;
  }

  function pointerMove(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const canvas = event.currentTarget;
    const context = canvas.getContext("2d");
    if (!context) return;
    const point = getPoint(event);
    const previous = pointsRef.current.at(-1);
    pointsRef.current.push(point);
    if (previous) {
      drawStroke(context, { playerId: room.you, color: "#11140f", width: 5, points: [previous, point] }, canvas.width, canvas.height);
    }
  }

  function pointerUp() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const points = pointsRef.current;
    pointsRef.current = [];
    if (points.length >= 2) {
      locallyDrawnStrokesRef.current.push({ playerId: room.you, color: "#11140f", width: 5, points });
      action("draw-stroke", { color: "#11140f", width: 5, points });
    }
  }

  function sendGuess() {
    if (!guess.trim()) return;
    action("draw-guess", { text: guess });
    setGuess("");
  }

  return (
    <section className="play-panel draw-scene">
      <PanelTitle title="你画我猜" subtitle="房主开局后轮流当画手，其他人抢猜" icon={<Pencil size={22} />} meta={`第 ${game.round || 0} 题 · 题库 ${game.bankSize} 题`} />
      <div className="draw-status">
        <span>画手：{game.drawerName || "等待开局"}</span>
        <strong>{game.myPrompt ? `题目：${game.myPrompt}` : isDrawer ? "等待题目" : "看画猜词"}</strong>
      </div>
      <canvas
        ref={canvasRef}
        className={isDrawer ? "draw-canvas can-draw" : "draw-canvas"}
        width={640}
        height={420}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
      />
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-draw")}>
            <Play size={18} />
            {game.round === 0 ? "开始作画" : "下一题"}
          </button>
        )}
        {(isHost || isDrawer) && (
          <button className="ghost-btn" onClick={() => action("clear-draw")}>
            <RefreshCw size={18} />
            清屏
          </button>
        )}
        {isHost && game.phase === "draw" && (
          <button className="ghost-btn" onClick={() => action("reveal-draw")}>
            <Eye size={18} />
            公布题目
          </button>
        )}
      </div>
      {!isDrawer && (
        <>
          <p className="microcopy">线下聚会建议直接口头抢答，互动感更强；远程游玩可在这里提交答案。</p>
          {myCorrectGuess && <div className="answer-band">猜对了！答案就是“{myCorrectGuess.text}” 🎉</div>}
          <div className="note-row">
            <input
              value={guess}
              onChange={(event) => setGuess(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && sendGuess()}
              placeholder={myCorrectGuess ? "本题已猜对" : "输入猜测"}
              maxLength={40}
              disabled={Boolean(myCorrectGuess) || game.phase !== "draw"}
            />
            <button className="icon-btn wide" onClick={sendGuess} disabled={Boolean(myCorrectGuess) || game.phase !== "draw"}>
              抢猜
            </button>
          </div>
        </>
      )}
      <HistoryList items={game.guesses.map((item) => `${item.playerName}: ${item.text} ${item.correct ? "✓" : ""}`)} />
    </section>
  );
}

function TruthPanel({ game, action }: { game: Extract<PublicGame, { gameId: "truth" }>; action: (type: string, payload?: unknown) => void }) {
  return (
    <section className="play-panel truth-scene">
      <PanelTitle title="真心话大冒险" subtitle="抽到的人现场完成，适合局间破冰" icon={<MessageCircleQuestion size={22} />} meta={`第 ${game.round || 0} 轮 · 真心话 ${game.truthBankSize} / 大冒险 ${game.dareBankSize}`} />
      <div className="prompt-box">
        <span>{game.current?.mode || "等待抽取"}</span>
        <strong>{game.current?.text || "选真心话、大冒险，或者随机。"}</strong>
      </div>
      <div className="action-row">
        <button className="primary-btn" onClick={() => action("draw-truth", { mode: "random" })}>
          <Play size={18} />
          随机抽
        </button>
        <button className="ghost-btn" onClick={() => action("draw-truth", { mode: "真心话" })}>
          真心话
        </button>
        <button className="ghost-btn" onClick={() => action("draw-truth", { mode: "大冒险" })}>
          大冒险
        </button>
      </div>
      <HistoryList items={game.history.map((item) => `${item.playerName} 抽到${item.mode}: ${item.text}`)} />
    </section>
  );
}

function NeverPanel({ game, action }: { game: Extract<PublicGame, { gameId: "never" }>; action: (type: string) => void }) {
  return (
    <section className="play-panel never-scene">
      <PanelTitle title="我有你没有" subtitle="说中经历的人举手，少数方接受惩罚" icon={<Hand size={22} />} meta={`第 ${game.round || 0} 轮 · 题库 ${game.bankSize} 题`} />
      <div className="prompt-box">
        <span>{game.current?.playerName || "等待抽题"}</span>
        <strong>{game.current?.text || "抽一条经历题，现场举手判断。"}</strong>
      </div>
      <button className="primary-btn" onClick={() => action("draw-never")}>
        <Play size={18} />
        抽题
      </button>
      <HistoryList items={game.history.map((item) => `${item.playerName}: ${item.text}`)} />
    </section>
  );
}

function ForbiddenPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "forbidden" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string) => void;
}) {
  return (
    <section className="play-panel forbidden-scene">
      <PanelTitle title="禁词挑战（害你在心口难开）" subtitle="看别人的触发目标，诱导对方说出或做出" icon={<Ban size={22} />} meta={`第 ${game.round || 0} 局 · 题库 ${game.bankSize} 个`} />
      <p className="microcopy">
        推荐线下把目标写在便利贴贴到额头，自己看不到自己的目标，互动感更强；不贴纸也可以按下面姓名对应现场朋友。
      </p>
      {game.phase === "answer" && (
        <div className="secret-card forbidden-card">
          <span>你的触发目标</span>
          <strong>{game.myAssignmentRevealed || "暂无"}</strong>
        </div>
      )}
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-forbidden")}>
            <Play size={18} />
            发禁词
          </button>
        )}
        {isHost && game.phase === "play" && (
          <button className="ghost-btn" onClick={() => action("reveal-forbidden")}>
            <Eye size={18} />
            公布全部目标
          </button>
        )}
      </div>
      {game.assignedCount > 0 && (
        <div className="whoami-grid">
          {room.players.filter((player) => player.id !== room.you).map((player) => (
            <div className="whoami-card" key={player.id}>
              <span className="avatar-dot" style={{ background: player.color }} />
              <strong>{player.name}</strong>
              <em>{game.othersAssignments[player.id] || "—"}</em>
            </div>
          ))}
        </div>
      )}
      <p className="microcopy">对方触发自己头上的语言或动作即淘汰；不要直接把目标念给对方听。</p>
    </section>
  );
}

function ReactionPanel({ game, action }: { game: Extract<PublicGame, { gameId: "reaction" }>; action: (type: string) => void }) {
  return (
    <section className="play-panel reaction-scene">
      <PanelTitle title="反应小游戏" subtitle="007、逛三园、数字炸弹等线下快节奏玩法" icon={<Zap size={22} />} meta={`第 ${game.round || 0} 轮 · 题库 ${game.bankSize} 条`} />
      <div className="prompt-box">
        <span>{game.current?.playerName || "等待抽取"}</span>
        <strong>{game.current?.text || "抽一条现场规则，马上开始。"}</strong>
      </div>
      <button className="primary-btn" onClick={() => action("draw-reaction")}>
        <Play size={18} />
        抽玩法
      </button>
      <HistoryList items={game.history.map((item) => `${item.playerName}: ${item.text}`)} />
    </section>
  );
}

function WhoAmIPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "whoami" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const [guess, setGuess] = useState("");
  const others = room.players.filter((p) => p.id !== room.you);
  const myGuessed = game.guesses.find((g) => g.playerId === room.you && g.correct);
  function submitGuess() {
    const text = guess.trim();
    if (!text) return;
    action("guess-whoami", { guess: text });
    setGuess("");
  }
  return (
    <section className="play-panel whoami-scene">
      <PanelTitle title="我是谁（猜身份）" subtitle="头上贴名字，靠是/否问题猜自己的身份" icon={<Users size={22} />} meta={`第 ${game.round || 0} 局 · 题库 ${game.bankSize} 个`} />
      <p className="microcopy">{game.phase === "idle" ? "由房主发牌后，每人会看到其他人头上的身份。" : "通过提问推断你头上的身份；猜对脱出。"}</p>
      <div className="whoami-grid">
        {others.map((player) => (
          <div className="whoami-card" key={player.id}>
            <span className="avatar-dot" style={{ background: player.color }} />
            <strong>{player.name}</strong>
            <em>{game.othersAssignments[player.id] || "—"}</em>
          </div>
        ))}
      </div>
      <div className="secret-card">
        <span>你头上的身份</span>
        <strong>{game.phase === "answer" ? game.myAssignmentRevealed || "暂无" : myGuessed ? `已猜对：${myGuessed.guess}` : "等你猜"}</strong>
      </div>
      <div className="action-row input-row">
        <input
          value={guess}
          onChange={(event) => setGuess(event.target.value)}
          placeholder="说出你猜的身份"
          maxLength={24}
          disabled={game.phase !== "play" || Boolean(myGuessed)}
        />
        <button className="icon-btn wide" onClick={submitGuess} disabled={game.phase !== "play" || Boolean(myGuessed)}>
          <Play size={16} />
          提交
        </button>
      </div>
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-whoami")}>
            <Play size={18} />
            {game.phase === "idle" ? "发牌开局" : "重新发牌"}
          </button>
        )}
        {isHost && game.phase === "play" && (
          <button className="ghost-btn" onClick={() => action("reveal-whoami")}>
            <Eye size={18} />
            公布身份
          </button>
        )}
      </div>
      <HistoryList items={game.guesses.map((item) => `${item.playerName}: ${item.guess} ${item.correct ? "✓" : "✗"}`)} />
    </section>
  );
}

function CharadesPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "charades" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const [guess, setGuess] = useState("");
  const isPerformer = room.you === game.performerId;
  const myCorrectGuess = game.guesses.find((item) => item.playerId === room.you && item.correct);
  const performerLabel = game.performerName
    ? isPerformer
      ? `本轮表演者：你（${game.performerName}）`
      : `本轮表演者：${game.performerName}`
    : "等待房主开始下一题";
  function submitGuess() {
    const text = guess.trim();
    if (!text) return;
    action("charades-guess", { text });
    setGuess("");
  }
  return (
    <section className="play-panel charades-scene">
      <PanelTitle title="你演我猜" subtitle="表演者只用动作，其他人抢猜" icon={<Sparkles size={22} />} meta={`第 ${game.round || 0} 题 · 题库 ${game.bankSize} 条 · 表演者 ${game.performerName || "—"}`} />
      <div className="performer-banner">
        <span>当前表演者</span>
        <strong>{performerLabel}</strong>
      </div>
      <p className="microcopy">线下聚会建议直接口头抢答，互动感更强；远程游玩可在下方提交答案。</p>
      {myCorrectGuess && <div className="answer-band">猜对了！答案就是“{myCorrectGuess.text}” 🎉</div>}
      <div className="prompt-box">
        <span>{isPerformer ? "你的题目" : game.phase === "answer" ? "本题答案" : "看动作猜词"}</span>
        <strong>{isPerformer || game.phase === "answer" ? game.myPrompt || "等待开局" : "请专注观察当前表演者的动作"}</strong>
      </div>
      <div className="action-row input-row">
        <input
          value={guess}
          onChange={(event) => setGuess(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && submitGuess()}
          placeholder={isPerformer ? "你是表演者，不要输入" : "输入你的猜测"}
          maxLength={40}
          disabled={isPerformer || game.phase !== "play" || Boolean(myCorrectGuess)}
        />
        <button className="icon-btn wide" onClick={submitGuess} disabled={isPerformer || game.phase !== "play" || Boolean(myCorrectGuess)}>
          <Play size={16} />
          抢猜
        </button>
      </div>
      <div className="action-row">
        {isHost && (
          <button className="primary-btn" onClick={() => action("start-charades")}>
            <Play size={18} />
            {game.phase === "idle" ? "开始表演" : "下一题"}
          </button>
        )}
        {isHost && game.phase === "play" && (
          <button className="ghost-btn" onClick={() => action("reveal-charades")}>
            <Eye size={18} />
            公布答案
          </button>
        )}
      </div>
      <HistoryList items={game.guesses.map((item) => `${item.playerName}: ${item.text} ${item.correct ? "✓" : ""}`)} />
    </section>
  );
}

function TwentyPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "twenty" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const [question, setQuestion] = useState("");
  const [guess, setGuess] = useState("");
  const isAnswerer = room.you === game.answererId;
  const myCorrectGuess = game.guesses.find((item) => item.playerId === room.you && item.correct);
  const submitQuestion = () => {
    const text = question.trim();
    if (!text) return;
    action("twenty-question", { text });
    setQuestion("");
  };
  const submitGuess = () => {
    const text = guess.trim();
    if (!text) return;
    action("twenty-guess", { text });
    setGuess("");
  };
  return (
    <section className="play-panel twenty-scene">
      <PanelTitle title="秘密词二十问（是否问答）" subtitle="一人知道秘密词，其他人合计最多问 20 个问题" icon={<CircleHelp size={22} />} meta={`第 ${game.round || 0} 题 · 已问 ${game.questions.length}/20 · 题库 ${game.bankSize} 个`} />
      <div className="performer-banner"><span>回答者</span><strong>{game.answererName || "等待房主开题"}</strong></div>
      <div className="prompt-box">
        <span>{isAnswerer ? "你的秘密词" : game.phase === "answer" ? "本题答案" : "通过是/否问题推理"}</span>
        <strong>{game.myPrompt || (isAnswerer ? "等待开题" : "答案只对回答者可见")}</strong>
      </div>
      <p className="microcopy">线下建议直接口头问答；页面记录问题与最终猜测。分组、倒计时和总分需要主持人现场执行。</p>
      {!isAnswerer && (
        <>
          {myCorrectGuess && <div className="answer-band">猜对了！答案就是“{myCorrectGuess.text}” 🎉</div>}
          <div className="note-row">
            <input value={question} onChange={(event) => setQuestion(event.target.value)} placeholder={game.questions.length >= 20 ? "本题已问满 20 个问题" : "记录一个是/否问题"} maxLength={60} disabled={game.phase !== "play" || game.questions.length >= 20} />
            <button className="icon-btn wide" onClick={submitQuestion} disabled={game.phase !== "play" || game.questions.length >= 20}>提问</button>
          </div>
          <div className="note-row">
            <input value={guess} onChange={(event) => setGuess(event.target.value)} placeholder="提交最终猜测" maxLength={40} disabled={game.phase !== "play" || Boolean(myCorrectGuess)} />
            <button className="icon-btn wide" onClick={submitGuess} disabled={game.phase !== "play" || Boolean(myCorrectGuess)}>猜答案</button>
          </div>
        </>
      )}
      <div className="action-row">
        {isHost && <button className="primary-btn" onClick={() => action("start-twenty")}><Play size={18} />{game.phase === "idle" ? "开始一题" : "下一题"}</button>}
        {isHost && game.phase === "play" && <button className="ghost-btn" onClick={() => action("reveal-twenty")}><Eye size={18} />公布答案</button>}
      </div>
      <HistoryList items={[...game.guesses.map((item) => `${item.playerName} 猜：${item.text} ${item.correct ? "✓" : "✗"}`), ...game.questions.map((item) => `${item.playerName} 问：${item.text}`)]} />
    </section>
  );
}

function DescribePanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "describe" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const [guess, setGuess] = useState("");
  const isSpeaker = room.you === game.speakerId;
  const myCorrectGuess = game.guesses.find((item) => item.playerId === room.you && item.correct);
  const submitGuess = () => {
    const text = guess.trim();
    if (!text) return;
    action("describe-guess", { text });
    setGuess("");
  };
  return (
    <section className="play-panel describe-scene">
      <PanelTitle title="你说我猜" subtitle="用语言描述，但不能说出题目里的字" icon={<MessageCircleQuestion size={22} />} meta={`第 ${game.round || 0} 题 · 题库 ${game.bankSize} 个`} />
      <div className="performer-banner"><span>描述者</span><strong>{game.speakerName || "等待房主开题"}</strong></div>
      <div className="prompt-box">
        <span>{isSpeaker ? "你的题目" : game.phase === "answer" ? "本题答案" : "听描述猜词"}</span>
        <strong>{game.myPrompt || (isSpeaker ? "等待开题" : "请听描述者提示")}</strong>
      </div>
      <p className="microcopy">线下聚会建议直接口头抢答，互动感更强；远程游玩可在下方提交答案。</p>
      {!isSpeaker && (
        <>
          {myCorrectGuess && <div className="answer-band">猜对了！答案就是“{myCorrectGuess.text}” 🎉</div>}
          <div className="note-row">
            <input value={guess} onChange={(event) => setGuess(event.target.value)} onKeyDown={(event) => event.key === "Enter" && submitGuess()} placeholder="输入你的猜测" maxLength={40} disabled={game.phase !== "play" || Boolean(myCorrectGuess)} />
            <button className="icon-btn wide" onClick={submitGuess} disabled={game.phase !== "play" || Boolean(myCorrectGuess)}>抢猜</button>
          </div>
        </>
      )}
      <div className="action-row">
        {isHost && <button className="primary-btn" onClick={() => action("start-describe")}><Play size={18} />{game.phase === "idle" ? "开始描述" : "下一题"}</button>}
        {isHost && game.phase === "play" && <button className="ghost-btn" onClick={() => action("reveal-describe")}><Eye size={18} />公布答案</button>}
      </div>
      <HistoryList items={game.guesses.map((item) => `${item.playerName}: ${item.text} ${item.correct ? "✓" : ""}`)} />
    </section>
  );
}

function QuizPanel({
  game,
  room,
  isHost,
  action
}: {
  game: Extract<PublicGame, { gameId: "quiz" }>;
  room: RoomSnapshot;
  isHost: boolean;
  action: (type: string, payload?: unknown) => void;
}) {
  const [answer, setAnswer] = useState("");
  const myLatest = game.guesses.find((item) => item.playerId === room.you);
  const submitAnswer = (text = answer) => {
    const value = text.trim();
    if (!value) return;
    action("quiz-answer", { text: value });
    setAnswer("");
  };
  return (
    <section className="play-panel quiz-scene">
      <PanelTitle title="知识问答" subtitle="常识题抢答；页面反馈正误，分组总分由主持人记录" icon={<Brain size={22} />} meta={`第 ${game.round || 0} 题 · 题库 ${game.bankSize} 题`} />
      <div className="prompt-box"><span>{game.phase === "answer" ? "答案" : "题目"}</span><strong>{game.question || "等待房主抽题"}</strong></div>
      {game.options.length > 0 && game.phase === "play" && (
        <div className="action-row">
          {game.options.map((option) => <button className="ghost-btn" key={option} onClick={() => submitAnswer(option)}>{option}</button>)}
        </div>
      )}
      <p className="microcopy">线下聚会建议直接口头抢答，互动感更强；远程游玩可点击选项或输入答案。</p>
      {myLatest && <div className="answer-band">{myLatest.correct ? `回答正确！“${myLatest.text}”就是答案 🎉` : `“${myLatest.text}”不正确，再想想。`}</div>}
      <div className="note-row">
        <input value={answer} onChange={(event) => setAnswer(event.target.value)} onKeyDown={(event) => event.key === "Enter" && submitAnswer()} placeholder="输入答案" maxLength={40} disabled={game.phase !== "play"} />
        <button className="icon-btn wide" onClick={() => submitAnswer()} disabled={game.phase !== "play"}>提交</button>
      </div>
      {game.answer && <div className="secret-card"><span>正确答案</span><strong>{game.answer}</strong>{game.explanation && <em>{game.explanation}</em>}</div>}
      <div className="action-row">
        {isHost && <button className="primary-btn" onClick={() => action("start-quiz")}><Play size={18} />{game.phase === "idle" ? "抽一道题" : "下一题"}</button>}
        {isHost && game.phase === "play" && <button className="ghost-btn" onClick={() => action("reveal-quiz")}><Eye size={18} />公布答案</button>}
      </div>
      <HistoryList items={game.guesses.map((item) => `${item.playerName}: ${item.text} ${item.correct ? "✓" : "✗"}`)} />
    </section>
  );
}

function PanelTitle({ title, subtitle, icon, meta }: { title: string; subtitle: string; icon: React.ReactNode; meta?: string }) {
  return (
    <div className="panel-title">
      <div className="panel-icon">{icon}</div>
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {meta && <span className="title-meta">{meta}</span>}
    </div>
  );
}

function PlayingCard({ card }: { card: Card }) {
  const red = card.suit === "heart" || card.suit === "diamond";
  return (
    <div className={red ? "playing-card red" : "playing-card"}>
      <span>{card.label.split(" ")[0]}</span>
      <strong>{card.rank}</strong>
    </div>
  );
}

function HistoryList({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <ol className="history-list">
      {items.slice(0, 10).map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ol>
  );
}

function AdSlot() {
  // AdSense 接入能力保留但默认关闭。只有部署时同时配置
  // VITE_ADSENSE_CLIENT 与 VITE_ADSENSE_SLOT 才加载广告脚本和广告位。
  const adsenseClient = import.meta.env.VITE_ADSENSE_CLIENT as string | undefined;
  const adsenseSlot = import.meta.env.VITE_ADSENSE_SLOT as string | undefined;
  useEffect(() => {
    if (!adsenseClient || !adsenseSlot) return;
    if (!document.querySelector("script[data-adsbygoogle]")) {
      const tag = document.createElement("script");
      tag.async = true;
      tag.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseClient}`;
      tag.crossOrigin = "anonymous";
      tag.dataset.adsbygoogle = "1";
      document.head.appendChild(tag);
    }
    try {
      ((window as unknown as { adsbygoogle: unknown[] }).adsbygoogle ||= []).push({});
    } catch {
      // 广告加载失败不能影响房间游戏。
    }
  }, [adsenseClient, adsenseSlot]);
  if (adsenseClient && adsenseSlot) {
    return (
      <aside className="ad-slot ad-slot-live" aria-label="广告">
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%" }}
          data-ad-client={adsenseClient}
          data-ad-slot={adsenseSlot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </aside>
    );
  }
  return null;
}

function HomeIntro() {
  return (
    <section className="home-intro" aria-label="玩法介绍">
      <h2>16 款聚会玩法，扫码即玩</h2>
      <p>
        「聚会开局」是面向聚餐与团建场景的网页端房间游戏。主持人创建房间后，其他人通过扫码或 4 位房间码加入，
        每人手机上看自己的牌面，<strong>所有功能免费开放、无广告、无需下载 App、无需注册登录</strong>。当前共支持 16 款互动玩法，覆盖语言推理、
        身份隐藏、节奏反应、绘画沟通等不同类型，2-10 人均可。
      </p>
      <ul className="intro-grid">
        <li><strong>谁是卧底</strong>多数人拿同词，少数卧底拿相近词，轮流描述找出卧底。</li>
        <li><strong>狼人杀发牌</strong>屏幕负责发身份和阶段提示，发言投票在线下完成。</li>
        <li><strong>掷骰子</strong>实时多人骰盅，可作各类比大小、定惩罚的判定工具。</li>
        <li><strong>小姐牌</strong>按顺序轮流抽牌，抽到的牌按现场约定执行。</li>
        <li><strong>记牌挑战</strong>顺序抽牌后复述前面牌面，错了挨惩罚。</li>
        <li><strong>海龟汤</strong>主持人读汤面，其他人问是/否问题推理真相。</li>
        <li><strong>你画我猜</strong>当前画手看到题目作画，其他人抢猜。</li>
        <li><strong>真心话大冒险</strong>抽真心话/大冒险/随机题，最近记录可查。</li>
        <li><strong>我有你没有</strong>"我从来没有"经历题，做过的人举手。</li>
        <li><strong>禁词挑战（害你在心口难开）</strong>看别人的触发目标，诱导对方说出或做出。</li>
        <li><strong>反应小游戏</strong>007/三园等节奏类规则，反应慢的人受罚。</li>
        <li><strong>我是谁（猜身份）</strong>额头贴别人能看到的身份，自己问是/否问题猜出身份。</li>
        <li><strong>你演我猜</strong>表演者用动作演，其他人抢答，不能说话。</li>
        <li><strong>秘密词二十问（是否问答）</strong>一人知道秘密词，其他人用最多 20 个是/否问题推理。</li>
        <li><strong>你说我猜</strong>描述者不能说出题目中的字，其他人抢猜。</li>
        <li><strong>知识问答</strong>抽取常识题，线下口头或线上提交抢答。</li>
      </ul>
      <p className="intro-tip">
        想直接玩谁是卧底？先看 <a href="/online-who-is-undercover">免费在线玩谁是卧底：多人扫码开房说明</a>，包含价格、人数、词库、使用步骤和限制。
      </p>
      <p className="intro-tip">
        也可以从 <a href="/play">免费在线聚会游戏入口</a> 按使用需求选择狼人杀发牌、你画我猜、海龟汤或真心话大冒险。
      </p>
      <p className="intro-tip">
        想了解每款玩法的<strong>详细规则、技巧和示例</strong>？查看 <a href="/games">全部游戏攻略 →</a>，或者阅读 <a href="/guides">场景化实用指南 →</a>
      </p>
      <p className="intro-tip">
        <strong>三步开局</strong>：① 主持人创建房间、选一款游戏，拿到一个 4 位房间码；② 其他人扫码或输入房间码加入，不用下载 App、不用注册；③ 每人在自己手机上看牌面/题目，发言、表演、投票都在桌边面对面进行。
      </p>
      <p className="intro-tip">
        不知道玩什么？按场景看 <a href="/games/team-building-games">团建破冰游戏怎么选</a>，按人数看 <a href="/games/party-games-by-size">2–10 人聚会游戏推荐</a>，新手主持可读 <a href="/games/how-to-host">主持人带气氛技巧</a>。
      </p>
      <p className="intro-tip">
        如果你负责组织整场活动，可以先看 <a href="/hosting-checklist">聚会前 10 分钟准备清单</a> 和 <a href="/safety">聚会游戏礼仪与安全边界</a>；遇到房间、重连或隐私问题，集中说明见 <a href="/faq">FAQ</a>。
      </p>
      <p className="intro-tip">
        房间数据仅用于维持当前游戏和断线重连，解散或超时后清理；短期恢复快照与访问统计说明见 <a href="/privacy">隐私政策</a>。
      </p>
      <section className="product-facts" aria-labelledby="product-facts-title">
        <h2 id="product-facts-title">产品信息一览</h2>
        <dl>
          <div><dt>当前版本</dt><dd>v1.7.0</dd></div>
          <div><dt>最后更新</dt><dd><time dateTime="2026-07-30">2026 年 7 月 30 日</time></dd></div>
          <div><dt>游戏数量</dt><dd>16 款</dd></div>
          <div><dt>谁是卧底词库</dt><dd>14,000+ 词对</dd></div>
          <div><dt>支持人数</dt><dd>2-10 人，具体以游戏为准</dd></div>
          <div><dt>费用与广告</dt><dd>所有功能免费开放，全站免费无广告</dd></div>
          <div><dt>注册</dt><dd>无需注册登录</dd></div>
          <div><dt>断线重连</dt><dd>同一设备、同一浏览器且未清除网站数据时，离线约 30 分钟内通常可恢复原座位；无需使用同一网络</dd></div>
          <div><dt>维护者</dt><dd>邹晨振 / zouchenzhen</dd></div>
          <div>
            <dt>问题与体验反馈</dt>
            <dd><a href="https://github.com/zouchenzhen/party-games-online/issues" target="_blank" rel="noreferrer">GitHub Issues</a> 或 <a href="mailto:admin@xiaozou.qzz.io">admin@xiaozou.qzz.io</a></dd>
          </div>
        </dl>
      </section>
      <section className="home-faq" aria-labelledby="home-faq-title">
        <h2 id="home-faq-title">常见问题</h2>
        <dl>
          <div>
            <dt>聚会开局收费吗？</dt>
            <dd>当前 16 款玩法的全部功能均免费开放，不要求购买会员，页面不展示广告。</dd>
          </div>
          <div>
            <dt>需要下载 App、微信小程序或注册登录吗？</dt>
            <dd>不需要。房主和玩家都直接使用手机浏览器，输入昵称即可创建或加入房间。</dd>
          </div>
          <div>
            <dt>其他玩家怎么加入同一个房间？</dt>
            <dd>房主开房后会获得二维码和 4 位房间码；其他人扫码，或打开同一网址输入房间码加入。</dd>
          </div>
          <div>
            <dt>每个人都要用自己的手机吗？</dt>
            <dd>建议每人一部手机。谁是卧底、狼人杀等玩法会把私密词语或身份分别显示在各自屏幕上，不用轮流传一部手机。</dd>
          </div>
          <div>
            <dt>支持哪些聚会游戏？</dt>
            <dd>共 16 款：谁是卧底、狼人杀、掷骰子、小姐牌、记牌挑战、海龟汤、你画我猜、真心话大冒险、我有你没有、禁词挑战（害你在心口难开）、反应小游戏、我是谁（猜身份）、你演我猜、秘密词二十问（是否问答）、你说我猜和知识问答。</dd>
          </div>
          <div>
            <dt>房间断线后还能回来吗？</dt>
            <dd>同一设备、同一浏览器且未清除网站数据时，离线约 30 分钟内通常可自动恢复原座位；切换 Wi‑Fi 或移动网络不影响恢复。超过宽限期、主动退出、房主解散、清除网站数据、换浏览器或使用已关闭的无痕窗口后，可能需要重新加入。</dd>
          </div>
        </dl>
        <p className="intro-tip">更多关于隐私、兼容性和主持方式的说明见 <a href="/faq">完整 FAQ →</a></p>
      </section>
      {/* ADSENSE-REVIEW 临时隐藏（含 workers.dev 镜像外链，过审后取消注释即恢复） */}
      {/* <p className="backup-note">
        🔗 <strong>备用线路</strong>：主站打不开或连不上时，可改用备用线路继续玩 —— 通用线路 <a href="https://xiaozou.qzz.io" rel="noreferrer">xiaozou.qzz.io</a>，海外用户可用 <a href="https://party-room-games-cf.zouchenzhen.workers.dev" rel="noreferrer">party-room-games-cf.zouchenzhen.workers.dev</a>（两条备用线路互通、房间共享）。<strong>备用线路与主站房间相互独立</strong>，请整桌人统一使用主站、或统一使用备用线路。
      </p> */}
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer" aria-label="站点页脚">
      <div className="site-footer-row">
        <a href="/online-who-is-undercover">在线玩谁是卧底</a>
        <a href="/play">在线玩法</a>
        <a href="/games">游戏攻略</a>
        <a href="/guides">实用指南</a>
        <a href="/hosting-checklist">主持清单</a>
        <a href="/faq">FAQ</a>
        <a href="/about">关于</a>
        <a href="/updates">更新日志</a>
        <a href="/privacy">隐私政策</a>
        <a href="/terms">服务条款</a>
        <a href="/contact">联系</a>
      </div>
      {/* ADSENSE-REVIEW 临时隐藏（含 workers.dev 镜像外链，过审后取消注释即恢复） */}
      {/* <div className="site-footer-row">
        <span>主站异常时备用线路（与主站房间独立）：</span>
        <a href="https://xiaozou.qzz.io" rel="noreferrer">xiaozou.qzz.io（通用）</a>
        <a href="https://party-room-games-cf.zouchenzhen.workers.dev" rel="noreferrer">party-room-games-cf.zouchenzhen.workers.dev（海外用户）</a>
      </div> */}
      <div className="site-footer-row">
        {/* ADSENSE-REVIEW 临时隐藏（过审后取消注释即恢复）: <span>源码打包售卖 ¥1499/份</span> */}
        <a href="https://github.com/zouchenzhen/party-games-online" target="_blank" rel="noreferrer">GitHub 产品资料</a>
        <a href="mailto:admin@xiaozou.qzz.io">邮箱 admin@xiaozou.qzz.io</a>
      </div>
      <p className="site-footer-copy">© 2026 zouchenzhen · 个人独立开发</p>
    </footer>
  );
}

function InviteQr({ code, link, qr }: { code: string; link: string; qr: string }) {
  return (
    <section className="invite-card" aria-label="扫码加入房间">
      <div>
        <span>扫码加入</span>
        <strong>房间码 {code}</strong>
        <p>{link.includes("localhost") ? "手机扫码前请先用局域网 IP 打开网页，否则手机无法访问 localhost。" : "手机扫码后会自动带入房间码。"}</p>
      </div>
      {qr ? <img src={qr} alt={`加入房间 ${code} 的二维码`} /> : <div className="qr-placeholder">QR</div>}
    </section>
  );
}

function GameRules({ gameId }: { gameId: GameId }) {
  return (
    <section className="rules-card" aria-label="游戏规则">
      <span>简要规则与胜负</span>
      <ul>
        {gameRules[gameId].map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
      <a className="rules-detail-link" href={gameGuideLinks[gameId]}>查看详细规则、示例与注意事项 →</a>
    </section>
  );
}

function sameDrawStroke(left: DrawStroke, right: DrawStroke | undefined): boolean {
  if (!right || left.playerId !== right.playerId || left.color !== right.color || left.width !== right.width || left.points.length !== right.points.length) {
    return false;
  }
  return left.points.every(([leftX, leftY], index) => {
    const [rightX, rightY] = right.points[index];
    return leftX === rightX && leftY === rightY;
  });
}

function drawStroke(context: CanvasRenderingContext2D, stroke: DrawStroke, width: number, height: number) {
  if (stroke.points.length < 2) return;
  context.save();
  context.lineCap = "round";
  context.lineJoin = "round";
  context.strokeStyle = stroke.color;
  context.lineWidth = stroke.width;
  context.beginPath();
  stroke.points.forEach(([x, y], index) => {
    const px = x * width;
    const py = y * height;
    if (index === 0) context.moveTo(px, py);
    else context.lineTo(px, py);
  });
  context.stroke();
  context.restore();
}

function clamp(value: number) {
  return Math.min(1, Math.max(0, value));
}

// Coerce a numeric <input> value into [min,max], falling back to the prior value when the
// field is empty or non-numeric — so a cleared field never sends NaN to the server.
function clampInt(raw: string, min: number, max: number, fallback: number): number {
  if (raw.trim() === "") return fallback;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function maxUndercoverCount(playerCount: number): number {
  return Math.max(1, Math.min(playerCount - 2, Math.floor(playerCount / 3)));
}

// Copy helper that degrades gracefully. navigator.clipboard is only available in secure
// contexts (https / localhost); over a plain-http LAN IP (the README's multi-phone setup)
// it is undefined, so fall back to a temporary textarea + execCommand. Returns success.
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path below
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function phaseLabel(phase: string) {
  const labels: Record<string, string> = {
    idle: "待开始",
    reveal: "查看身份",
    night: "夜晚",
    day: "白天",
    vote: "投票",
    answer: "已揭晓",
    play: "进行中",
    draw: "作画中"
  };
  return labels[phase] || phase;
}
